import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from './supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session,  setSession]  = useState(null)
  const [user,     setUser]     = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [orgId,    setOrgId]    = useState(null)
  const [role,     setRole]     = useState(null)
  const [mfaEnabled, setMfaEnabled] = useState(false)
  // Session-scoped: true only after a successful TOTP challenge/verify THIS
  // session. Not persisted — a fresh page load starts false, forcing the
  // login TOTP step before /app is reachable (route enforcement below).
  const [mfaVerified, setMfaVerified] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) await loadUserMeta(session.user)
      setLoading(false)
    })

    // REGRESSION FIX (2026-08-17): do NOT toggle the global `loading` flag on
    // auth-state transitions. App.jsx unmounts the entire router while
    // `loading` is true, so flipping it on SIGNED_IN destroyed LoginPage's
    // in-progress MFA step state — a successful password submit bounced the
    // user straight back to an empty login form and the TOTP challenge never
    // fired (verified in edge/auth logs 2026-08-17 01:25Z). `loading` now
    // covers ONLY the initial getSession() resolution above.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) await loadUserMeta(session.user)
      else { setRole(null); setMfaEnabled(false); setOrgId(null); setMfaVerified(false) }
    })

    return () => subscription.unsubscribe()
  }, [])

  const loadUserMeta = async (user) => {
    // The shared saas schema is intentionally not API-exposed. Use its hardened
    // public invoker view, matching the other PatientTrac applications.
    const { data, error } = await supabase
      .schema('public')
      .from('org_members')
      .select('org_id, role, mfa_enabled, is_active')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (error) {
      console.error('loadUserMeta error:', error)
      setOrgId(null)
      setRole(null)
      setMfaEnabled(false)
      return
    }
    if (data) {
      setOrgId(data.org_id)
      setRole(data.role)
      setMfaEnabled(!!data.mfa_enabled)
    }
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  const signInMagicLink = async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/callback` }
    })
    if (error) throw error
  }

  const signOut = async () => { setMfaVerified(false); await supabase.auth.signOut() }

  // Called by LoginPage after the TOTP edge function confirms the code.
  const markMfaVerified = () => setMfaVerified(true)

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    if (error) throw error
  }

  return (
    <AuthContext.Provider value={{
      session, user, loading, orgId, role, mfaEnabled, mfaVerified,
      isAuthenticated: !!session,
      // MFA is satisfied when the user has none required OR verified this session.
      mfaSatisfied: !mfaEnabled || mfaVerified,
      signIn, signInMagicLink, signOut, resetPassword, markMfaVerified,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
